import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Sparkles, Target, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { renderRich } from "@/lib/renderRich";

const STORAGE_KEY = "thc-next-step-progress-v1";

function readProgress() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (_e) {
    return {};
  }
}

function writeProgress(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_e) {
    // ignore
  }
}

/**
 * Tarjeta que muestra la PRÓXIMA acción concreta del plan personalizado del
 * usuario, extraída del diagnóstico más reciente. Al marcar el paso como
 * hecho, se guarda por diagnóstico + índice en localStorage y aparece el
 * siguiente. Cuando se completan todos los pasos de todas las áreas
 * recomendadas, muestra un estado de "misión cumplida".
 */
export default function NextStepCard({ latestId, onOpenDetail }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(readProgress);

  // Recarga el detalle cuando cambia el id del último diagnóstico.
  useEffect(() => {
    if (!latestId) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/diagnostic/${latestId}`);
        if (!cancelled) setData(res.data);
      } catch (_e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [latestId]);

  // Aplanamos todos los pasos de todas las tarjetas del plan en una lista
  // ordenada, con contexto (nombre del área, etc.).
  const flatSteps = useMemo(() => {
    if (!data?.recommendations?.cards) return [];
    const out = [];
    for (const card of data.recommendations.cards) {
      const areaName = card.name || card.title || "Mantenimiento";
      const areaCode = card.code || null;
      (card.steps || []).forEach((step, idx) => {
        out.push({
          key: `${latestId}::${areaCode || "M"}::${idx}`,
          areaName,
          areaCode,
          areaLevel: card.level,
          step,
        });
      });
    }
    return out;
  }, [data, latestId]);

  const doneMap = progress[latestId] || {};
  const nextIdx = flatSteps.findIndex((s) => !doneMap[s.key]);
  const currentStep = nextIdx >= 0 ? flatSteps[nextIdx] : null;
  const completedCount = flatSteps.filter((s) => doneMap[s.key]).length;
  const totalCount = flatSteps.length;
  const pctDone = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  function markDone(step) {
    const next = { ...progress };
    next[latestId] = { ...(next[latestId] || {}), [step.key]: Date.now() };
    setProgress(next);
    writeProgress(next);
  }

  function undoLast() {
    if (!currentStep && totalCount) {
      // Todos hechos: desmarca el último para permitir "volver a hacer"
      const lastKey = flatSteps[flatSteps.length - 1].key;
      const next = { ...progress };
      next[latestId] = { ...(next[latestId] || {}) };
      delete next[latestId][lastKey];
      setProgress(next);
      writeProgress(next);
      return;
    }
    if (nextIdx <= 0) return;
    const prev = flatSteps[nextIdx - 1];
    const next = { ...progress };
    next[latestId] = { ...(next[latestId] || {}) };
    delete next[latestId][prev.key];
    setProgress(next);
    writeProgress(next);
  }

  // No hay diagnóstico completado con personalización → no mostramos nada.
  if (!latestId) return null;
  if (loading) {
    return (
      <div
        className="rounded-2xl border border-emerald-100 bg-emerald-50/50 px-5 py-4 flex items-center gap-3"
        data-testid="next-step-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin text-emerald-700" />
        <div className="text-sm text-emerald-900/80">Preparando tu próximo paso…</div>
      </div>
    );
  }
  if (!flatSteps.length) return null;

  const allDone = !currentStep;

  return (
    <div
      className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-white p-5 md:p-6"
      data-testid="next-step-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            {allDone ? (
              <Sparkles className="w-5 h-5" strokeWidth={1.75} />
            ) : (
              <Target className="w-5 h-5" strokeWidth={1.75} />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-700">
              {allDone ? "Misión cumplida" : "Tu siguiente paso"}
            </div>
            <div className="font-display font-semibold text-gray-900 text-[15px] leading-snug line-clamp-2">
              {allDone
                ? "Has completado el plan"
                : currentStep.areaName
                  ? `Área · ${currentStep.areaName}`
                  : "Micro-acción de esta semana"}
            </div>
          </div>
        </div>
        {/* Contador de progreso */}
        <div className="hidden sm:flex flex-col items-end flex-shrink-0">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider">Progreso</div>
          <div className="text-sm font-semibold text-gray-800">
            {completedCount} / {totalCount}
          </div>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="h-1.5 rounded-full bg-emerald-100 overflow-hidden mb-4">
        <div
          className="h-full bg-emerald-600 transition-all duration-500"
          style={{ width: `${pctDone}%` }}
        />
      </div>

      {allDone ? (
        <div>
          <p
            className="text-[15px] text-gray-700 leading-relaxed mb-4"
            style={{ textAlign: "justify", hyphens: "auto" }}
          >
            Has marcado como hechos los {totalCount} pasos del plan personalizado de tu último
            diagnóstico. Es un excelente momento para <strong>repetir el diagnóstico</strong> y ver
            cuánto ha cambiado tu puntuación.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onOpenDetail && onOpenDetail(latestId)}
              className="btn-secondary text-sm py-2 px-4"
              data-testid="next-step-review-btn"
            >
              Revisar mi plan <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={undoLast}
              className="btn-ghost text-sm"
              data-testid="next-step-undo-btn"
            >
              Deshacer última acción
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p
            className="text-[15px] text-gray-800 leading-relaxed mb-4"
            style={{ textAlign: "justify", hyphens: "auto" }}
            data-testid="next-step-text"
          >
            {renderRich(currentStep.step)}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => markDone(currentStep)}
              className="btn-primary text-sm py-2 px-4"
              data-testid="next-step-done-btn"
            >
              <CheckCircle2 className="w-4 h-4" strokeWidth={2} /> Marcar como hecho
            </button>
            <button
              onClick={() => onOpenDetail && onOpenDetail(latestId)}
              className="btn-ghost text-sm"
              data-testid="next-step-open-plan-btn"
            >
              Ver plan completo <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {completedCount > 0 && (
              <button
                onClick={undoLast}
                className="btn-ghost text-sm ml-auto"
                data-testid="next-step-undo-btn"
              >
                Deshacer última
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
